/**
 * Sign-in failure reporting (board PB-90, D135). Call `reportAuthFailure`
 * from every catch in the sign-in, two-step, code and reCAPTCHA paths. A
 * user's own mistake (wrong password or code, closing the Google window) is
 * skipped by `USER_ERROR_CODES`; anything else is sent to the
 * `reportAuthFailure` function, which saves it and alerts the super admins.
 *
 * Best-effort: never throws, never changes what the person sees.
 */
import { auth } from '@/firebase'
import { functionUrl } from '@/services/functionsUrl'
import { USER_ERROR_CODES, type AuthFailureStage } from '@shared/authFailures'

export function reportAuthFailure(
  stage: AuthFailureStage,
  error: unknown,
  extra: { email?: string | null } = {}
): void {
  const e = error as { code?: string; name?: string; message?: string }
  // Services often return only Firebase's message: "Firebase: Error (auth/x)."
  const code =
    e?.code ??
    (e?.message ?? String(error)).match(/\((auth\/[a-z0-9-]+)\)/)?.[1]
  if (code && USER_ERROR_CODES.has(code)) return

  const user = auth.currentUser
  const body = {
    stage,
    errorCode: code,
    errorName: e?.name,
    errorMessage: e?.message ?? String(error),
    email: extra.email ?? user?.email ?? undefined,
    uid: user?.uid,
    path: window.location.pathname,
    env: import.meta.env.MODE,
    online: navigator.onLine,
    standalone: window.matchMedia?.('(display-mode: standalone)').matches,
  }
  // Always visible in Eruda, even where logger output is off
  console.error('[AuthFailure]', stage, { code, message: body.errorMessage })
  try {
    fetch(functionUrl('reportAuthFailure'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(err => console.warn('[AuthFailure] report not sent', err))
  } catch (err) {
    console.warn('[AuthFailure] report not sent', err)
  }
}
